import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReferralModal from './ReferralModal';
import ReferralFormModal from './ReferralFormModal';
import DesktopNav from './nav/DesktopNav';
import MobileNav from './nav/MobileNav';
import CTASection from './nav/CTASection';

const NAV_LINKS = [
  { name: 'Home', href: '/' },
  {
    name: 'Services',
    href: '#',
    hasDropdown: true,
    dropdownKey: 'services',
    dropdownItems: [
      { name: 'All Services Overview', href: '/services/comprehensive-mental-health' },
      { name: 'Therapy Services (OMHC)', href: '/services/outpatient-mental-health' },
      { name: 'Medication Management', href: '/services/medication-management' },
      { name: 'Individualized Treatment Plans', href: '/services/treatment-plans' },
      { name: 'Adult & Minor Psychiatric Rehabilitation Program (PRP)', href: '/services/psychiatric-rehabilitation' },
      { name: 'Community Resources', href: '/services/community-resources' },
      { name: 'Comprehensive Assessment', href: '/services/comprehensive-assessment' },
      { name: 'Student Counseling', href: '/services/student-counseling' },
      { name: 'Anger Management', href: '/services/anger-management' },
      { name: 'Counseling and Parenting', href: '/services/parenting-counseling' },
      { name: 'Social & Community Engagement', href: '/services/social-engagement' },
      { name: 'Juvenile Justice', href: '/services/juvenile-justice' },
      { name: 'Case Management', href: '/services/case-management' },
      { name: 'Veteran Services', href: '/services/veteran-services' },
      { name: 'Telehealth Services', href: '/services/telehealth' },
      { name: 'Referrals', href: '/services/referrals' }
    ]
  },
  { name: 'Start Here', href: '/get-started' },
  {
    name: 'Resources',
    href: '/resources',
    hasDropdown: true,
    dropdownKey: 'resources',
    dropdownItems: [
      { name: 'Mental Health Resources', href: '/resources/mental-health' },
      { name: 'Intake & Consent Forms', href: '/intake' },
      { name: 'Depression Treatment', href: '/resources/depression-treatment' },
      { name: 'Anxiety Treatment', href: '/resources/anxiety-treatment' },
      { name: 'PTSD Support', href: '/resources/ptsd-support' },
      { name: 'Baltimore City Services', href: '/areas/baltimore-city' },
      { name: 'Baltimore County Services', href: '/areas/baltimore-county' }
    ]
  },
  { name: 'Insurance', href: '/insurance' },
  {
    name: 'About Us',
    href: '#',
    hasDropdown: true,
    dropdownKey: 'aboutus',
    dropdownItems: [
      { name: 'About SDORA', href: '/about' },
      { name: 'Our Team', href: '/team' },
      { name: 'Testimonials', href: '/testimonials' },
      { name: 'Accreditation & Affiliations', href: '#accreditation' },
      { name: 'Careers', href: '/careers' }
    ]
  },
  { name: 'FAQ', href: '/faq' },
  {
    name: 'For Providers',
    href: '#',
    hasDropdown: true,
    dropdownKey: 'providers',
    dropdownItems: [
      { name: 'Submit a Referral', href: '#', action: 'referralForm' },
      { name: 'Services Available for Referral', href: '/services/comprehensive-mental-health', skipParentHighlight: true },
      { name: 'Referral Requirements', href: '/providers/referral-requirements' },
      { name: 'Partner With SDORA', href: '/providers/partner' },
      { name: 'Provider Resources', href: '/providers/resources' }
    ]
  }
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          setShowStickyCTA(window.scrollY > 300);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`sticky top-[56px] z-40 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-white shadow-md'
      }`}
      data-testid="main-navigation"
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpenDropdown(null);
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center transition-all duration-300 ${
          isScrolled ? 'h-14' : 'h-20'
        }`}>
          {/* Logo */}
          <div className="flex-shrink-0 mr-6">
            <Link to="/" className="flex items-center" data-testid="nav-logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/sthkmul7_SDORA_Logo-copy.png"
                alt="SDORA Wellbeing"
                className={`w-auto transition-all duration-300 ${
                  isScrolled ? 'h-14' : 'h-24'
                }`}
              />
            </Link>
          </div>

          {/* Right side: Navigation + CTA */}
          <div className="flex items-center gap-4 2xl:gap-6">
            <DesktopNav
              navLinks={NAV_LINKS}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              onOpenReferralForm={() => setShowReferralForm(true)}
            />

            <CTASection
              onOpenReferralModal={() => setShowReferralModal(true)}
            />
          </div>

          {/* Mobile menu button */}
          <div className="2xl:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-blue-600"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <MobileNav
            navLinks={NAV_LINKS}
            openDropdown={openDropdown}
            setOpenDropdown={setOpenDropdown}
            onClose={() => setIsOpen(false)}
            onOpenReferralForm={() => setShowReferralForm(true)}
            onOpenReferralModal={() => setShowReferralModal(true)}
          />
        )}
      </div>

      {showReferralModal && (
        <ReferralModal onClose={() => setShowReferralModal(false)} />
      )}

      {showReferralForm && (
        <ReferralFormModal onClose={() => setShowReferralForm(false)} />
      )}

      {ReactDOM.createPortal(
        <Link
          to="/intake"
          className={`md:hidden fixed bottom-24 right-5 z-[60] flex items-center gap-2 pl-5 pr-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-full shadow-xl font-bold text-sm transition-all duration-300 ${
            showStickyCTA && !isOpen
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
          data-testid="sticky-mobile-get-started-btn"
          aria-hidden={!(showStickyCTA && !isOpen)}
        >
          <span>GET STARTED</span>
          <ArrowRight className="w-4 h-4 flex-shrink-0" />
        </Link>,
        document.body
      )}
    </nav>
  );
};

export default Navigation;
